import { notesService } from '@/services/api';
import { isGuid } from '@/utils/idHelpers';
import type { ApiError } from '@/types/api/errors';
import type { NoteFiltersParams } from '@/types/api/requests';
import type {
  Note,
  NoteListItem,
  NoteMapItem,
  CreateNoteData,
  UpdateNoteData,
} from '@/types/notes';
import type { Coordinates } from '@/types';
import {
  mapNoteFromApi,
  mapNoteListItemFromApi,
  mapNoteMapItemFromApi,
  noteToListItem,
  noteToMapItem,
} from '../mappers/noteMapper';
import {
  applyOpenDetailsPanel,
  applyCloseDetails,
  applyOpenFormPanel,
  applyCloseFormPanel,
  applyClearStaleSelection,
  handleSelectItem,
  withShareFormGuard,
  type PanelDraftState,
} from '@/artifacts/panelHelpers';

// ===== SLICE INTERFACE =====

export interface NotesSlice {
  notes: {
    items: NoteListItem[];
    mapNotes: NoteMapItem[];
    selectedId: string | null;
    detailedNote: Note | null;
    isLoading: boolean;
    hasLoaded: boolean;
    isSyncing: boolean;
    error: ApiError | null;
    lastSyncAt: Date | null;

    // Pagination
    hasNextPage: boolean;
    totalCount: number;
    currentPage: number;
    _lastFetchFilters: NoteFiltersParams | undefined;

    // ===== ArtifactSlice contract (R1 of artifact registry refactor) =====
    /** Whether the note details panel is open. */
    detailsIsOpen: boolean;
    /** Whether the note form is open (create/edit). */
    formIsOpen: boolean;
    /** Form mode discriminator. */
    formMode: 'create' | 'edit' | null;
    /** Id of the note currently being edited (null in create mode). */
    formEditingId: string | null;
    /** Anchor point for the form (location selection / map click). */
    formAnchorPoint: Coordinates | null;
    /** Whether the form has unsaved user input (synced from NoteFormPanel). */
    formIsDirty: boolean;

    /** Open the note details panel. Closes any open form and the cross-artifact panels. */
    openDetails: () => void;
    /** Close the note details panel. */
    closeDetails: () => void;
    /**
     * Open the note form.
     * Routes through `formConflictSlice` if a task form is open.
     */
    openForm: (mode: 'create' | 'edit', itemId?: string) => void;
    /** Close the note form and reset cross-cutting flags. */
    closeForm: () => void;
    /** Slice-specific helper: set the form anchor point without opening/closing the form. */
    setFormAnchorPoint: (point: Coordinates | null) => void;
    /** Update the dirty flag from the active form component. */
    setFormIsDirty: (value: boolean) => void;
    /** ArtifactSlice contract: thin alias over `selectNote` for registry-driven overlay open. */
    selectItem: (id: string) => void;

    // CRUD actions
    fetchNotes: () => Promise<void>;
    loadMoreNotes: () => Promise<void>;
    fetchNotesForMap: () => Promise<void>;
    /** Replaces mapNotes wholesale — used by public share hydration. */
    setMapNotes: (notes: NoteMapItem[]) => void;
    fetchNoteById: (id: string) => Promise<void>;
    createNote: (data: CreateNoteData) => Promise<string | null>;
    updateNote: (id: string, data: UpdateNoteData) => Promise<boolean>;
    deleteNote: (id: string) => Promise<boolean>;
    selectNote: (id: string | null) => void;

    // Attachment actions
    uploadAttachment: (noteId: string, file: File) => Promise<boolean>;
    deleteAttachment: (
      noteId: string,
      attachmentId: string
    ) => Promise<boolean>;
    uploadImage: (noteId: string, file: File) => Promise<string | null>;

    // Task-linked notes
    fetchNotesByTaskId: (taskId: string) => Promise<Note[]>;

    // State helpers
    setLoading: (loading: boolean) => void;
    setError: (error: ApiError | null) => void;
    clearError: () => void;
  };
}

// ===== TYPES FOR GET PARAMETER =====

interface StoreWithNoteFilters {
  notes: NotesSlice['notes'];
  noteFilters?: {
    search: string;
    visibility: string | null;
    colors: string[];
    authors: string[];
    dateFrom: Date | null;
    dateTo: Date | null;
    hasLocation: boolean | null;
    location: string;
    page: number;
    pageSize: number;
    sortBy: string;
    sortDescending: boolean;
  };
  // Cross-slice dependencies needed by ArtifactSlice actions
  tasks: {
    formIsOpen: boolean;
    detailsIsOpen: boolean;
    closeDetails: () => void;
    closeForm: () => void;
  };
  shareForm: { formIsOpen: boolean };
  formConflict: { request: (onProceed: () => void) => void };
  ui: {
    isSelectingNoteLocation: boolean;
    cancelTaskLocationSelection: () => void;
  };
  setHideOtherMarkers: (value: boolean) => void;
}

// ===== HELPER FUNCTIONS =====

/**
 * Build NoteFiltersParams from store filter state.
 * Converts frontend filter state to API query parameters.
 */
function buildFiltersParams(
  filters: StoreWithNoteFilters['noteFilters']
): NoteFiltersParams {
  if (!filters) return {};

  const params: NoteFiltersParams = {};

  if (filters.search) {
    params.search = filters.search;
  }
  if (filters.visibility) {
    params.visibility = filters.visibility;
  }
  if (filters.colors.length > 0) {
    params.colors = filters.colors.join(',');
  }
  if (filters.authors.length > 0) {
    params.authors = filters.authors.join(',');
  }
  if (filters.dateFrom) {
    params.dateFrom = filters.dateFrom.toISOString();
  }
  if (filters.dateTo) {
    params.dateTo = filters.dateTo.toISOString();
  }
  if (filters.hasLocation !== null) {
    params.hasLocation = filters.hasLocation;
  }
  if (filters.location) {
    params.location = filters.location;
  }
  if (filters.pageSize) {
    params.pageSize = filters.pageSize;
  }
  if (filters.sortBy) {
    params.sortBy = filters.sortBy;
  }
  // Always send sortDescending — truthy check would skip false (ascending)
  params.sortDescending = filters.sortDescending;

  return params;
}

/**
 * Upsert (or remove) a note's map marker in place, keeping `mapNotes` in sync
 * after a create/update without a refetch. Removes the marker when the note no
 * longer has coordinates. Mirrors the tasks slice's optimistic-cache approach.
 */
function syncMapNote(mapNotes: NoteMapItem[], note: Note): void {
  const mapItem = noteToMapItem(note);
  const index = mapNotes.findIndex((item) => item.id === note.id);
  if (mapItem) {
    if (index >= 0) {
      mapNotes[index] = mapItem;
    } else {
      mapNotes.push(mapItem);
    }
  } else if (index >= 0) {
    mapNotes.splice(index, 1);
  }
}

// ===== SLICE CREATOR =====
//
// Note: Immer wraps the entire composed store, so the underlying mutation
// surface includes every slice. The `set` parameter is widened so that the
// new ArtifactSlice actions can mutate cross-slice flags (e.g.
// `state.ui.isSelectingNoteLocation`) inside a single draft transaction.
type NotesSliceSetState = NotesSlice &
  PanelDraftState & {
    ui: {
      isSelectingNoteLocation: boolean;
    };
  };

export const createNotesSlice = (
  set: (fn: (state: NotesSliceSetState) => void) => void,
  get: () => StoreWithNoteFilters,
  _store: unknown
) => ({
  notes: {
    items: [] as NoteListItem[],
    mapNotes: [] as NoteMapItem[],
    selectedId: null as string | null,
    detailedNote: null as Note | null,
    isLoading: false,
    hasLoaded: false,
    isSyncing: false,
    error: null as ApiError | null,
    lastSyncAt: null as Date | null,

    // Pagination
    hasNextPage: false,
    totalCount: 0,
    currentPage: 1,
    _lastFetchFilters: undefined as NoteFiltersParams | undefined,

    // ===== ArtifactSlice contract (R1) =====
    detailsIsOpen: false,
    formIsOpen: false,
    formMode: null as 'create' | 'edit' | null,
    formEditingId: null as string | null,
    formAnchorPoint: null as Coordinates | null,
    formIsDirty: false,

    // ===== ArtifactSlice actions (migrated from uiSlice in R1) =====

    /**
     * Open the note details panel.
     *
     * Side effects (migrated from `uiSlice.openNoteDetail`):
     * - Cross-artifact mutual exclusion: closes task details + form.
     * - Closes the note form (form ↔ details exclusion).
     */
    openDetails: () => {
      // Single right-panel rule: guard-close the share config form (prompts
      // when dirty) before revealing the details panel.
      withShareFormGuard(get(), () => {
        const store = get();

        // Cross-artifact mutual exclusion. cancelTaskLocationSelection is
        // separate from tasks.closeForm because the placement banner can be
        // active before the task form is opened.
        store.tasks.closeForm();
        store.tasks.closeDetails();
        store.ui.cancelTaskLocationSelection();

        set((state) => {
          state.notes.formIsOpen = false;
          state.notes.formMode = null;
          state.notes.detailsIsOpen = true;
          applyOpenDetailsPanel(state);
        });

        // Restore other-marker visibility — opening details means we are no longer
        // editing, so the form's auto-hide must be lifted (mirrors closeForm).
        // Without this, save → detail leaves hideOtherMarkers stuck true and the
        // new note's marker stays hidden until a full page reload.
        store.setHideOtherMarkers(false);
      });
    },

    /** Close the note details panel. */
    closeDetails: () => {
      set((state) => applyCloseDetails(state, state.notes));
    },

    /**
     * Open the note form.
     *
     * Side effects (migrated from `uiSlice.openNoteForm`):
     * - Unsaved-changes guard via `formConflict.request`.
     * - Cross-artifact mutual exclusion: closes task details + form.
     * - Closes the note details panel.
     * - Hides other markers so the user can focus on note creation/edit.
     */
    openForm: (mode: 'create' | 'edit', itemId?: string) => {
      // Idempotent re-open: re-syncing the already-open form (e.g. the panel
      // remount on a map ↔ list/kanban view switch) is a no-op. It must not
      // hit the unsaved-changes guard (which would falsely prompt) nor rewrite
      // formMode (which would remount the panel and reset its size/fullscreen).
      const current = get().notes;
      if (
        current.formIsOpen &&
        current.formMode === mode &&
        current.formEditingId === (mode === 'edit' ? (itemId ?? null) : null)
      ) {
        return;
      }

      // Route through the universal unsaved-changes guard: a dirty competing
      // form prompts before discard; clean forms close silently first.
      get().formConflict.request(() => {
        const store = get();

        // Cross-artifact mutual exclusion. cancelTaskLocationSelection is
        // separate from tasks.closeForm because the placement banner can be
        // active before the task form is opened.
        store.tasks.closeForm();
        store.tasks.closeDetails();
        store.ui.cancelTaskLocationSelection();

        set((state) => {
          applyOpenFormPanel(state, { minimizeOnMobile: false });
          state.notes.formIsOpen = true;
          state.notes.formMode = mode;
          state.notes.formEditingId = mode === 'edit' ? (itemId ?? null) : null;
          state.notes.formIsDirty = false;
          // Mutual exclusion within notes: close note detail when opening form
          state.notes.detailsIsOpen = false;
        });

        // Auto-hide other markers when opening note form
        store.setHideOtherMarkers(true);
      });
    },

    /**
     * Close the note form.
     *
     * Side effects (migrated from `uiSlice.closeNoteForm`):
     * - Cancels `isSelectingNoteLocation`.
     * - Restores other-marker visibility.
     */
    closeForm: () => {
      if (!get().notes.formIsOpen) return;

      const store = get();

      set((state) => {
        state.notes.formIsOpen = false;
        state.notes.formMode = null;
        state.notes.formEditingId = null;
        state.notes.formAnchorPoint = null;
        state.notes.formIsDirty = false;
        state.ui.isSelectingNoteLocation = false;
        applyCloseFormPanel(state);
      });

      // Restore artifact visibility when the note form closes
      store.setHideOtherMarkers(false);
    },

    /**
     * Slice-specific helper: set the form anchor point without opening the form.
     * Mirrors the legacy `uiSlice.setNoteFormAnchorPoint` (also clears the
     * "selecting location" mode when an explicit point is set).
     */
    setFormAnchorPoint: (point: Coordinates | null) => {
      set((state) => {
        state.notes.formAnchorPoint = point;
        if (point !== null) {
          state.ui.isSelectingNoteLocation = false;
        }
      });
    },

    setFormIsDirty: (value: boolean) => {
      if (get().notes.formIsDirty === value) return;
      set((state) => {
        state.notes.formIsDirty = value;
      });
    },

    // ===== CRUD ACTIONS =====

    fetchNotes: async () => {
      set((state) => {
        state.notes.isLoading = true;
        state.notes.error = null;
      });

      try {
        const storeState = get();
        const filterParams = buildFiltersParams(storeState.noteFilters);
        filterParams.page = 1;

        // Save filter snapshot for loadMore consistency
        set((state) => {
          state.notes._lastFetchFilters = filterParams;
        });

        const response = await notesService.getAll(filterParams);

        set((state) => {
          state.notes.items = response.data.items.map(mapNoteListItemFromApi);
          state.notes.currentPage = 1;
          state.notes.hasNextPage =
            response.data.page < response.data.totalPages;
          state.notes.totalCount = response.data.totalCount;
          state.notes.isLoading = false;
          state.notes.hasLoaded = true;
          state.notes.lastSyncAt = new Date();
          applyClearStaleSelection(state.notes, () => {
            state.notes.detailedNote = null;
          });
        });
      } catch (error) {
        set((state) => {
          state.notes.isLoading = false;
          state.notes.hasLoaded = true;
          state.notes.error = error as ApiError;
        });
      }
    },

    loadMoreNotes: async () => {
      const storeState = get();
      if (!storeState.notes.hasNextPage || storeState.notes.isLoading) return;

      set((state) => {
        state.notes.isLoading = true;
      });

      const nextPage = storeState.notes.currentPage + 1;
      // Use saved filter snapshot — NOT live filter state (prevents race conditions)
      const params = { ...storeState.notes._lastFetchFilters, page: nextPage };

      try {
        const response = await notesService.getAll(params);
        const newItems = response.data.items.map(mapNoteListItemFromApi);

        set((state) => {
          state.notes.items.push(...newItems);
          state.notes.currentPage = nextPage;
          state.notes.hasNextPage =
            response.data.page < response.data.totalPages;
          state.notes.totalCount = response.data.totalCount;
          state.notes.isLoading = false;
          state.notes.lastSyncAt = new Date();
        });
      } catch (error) {
        set((state) => {
          state.notes.isLoading = false;
          state.notes.error = error as ApiError;
        });
      }
    },

    fetchNotesForMap: async () => {
      try {
        const response = await notesService.getForMap({});

        set((state) => {
          state.notes.mapNotes = response.data.map(mapNoteMapItemFromApi);
        });
      } catch (error) {
        console.error('[notesSlice] fetchNotesForMap failed:', error);
      }
    },

    setMapNotes: (notes: NoteMapItem[]) => {
      set((state) => {
        state.notes.mapNotes = notes;
      });
    },

    fetchNoteById: async (id: string) => {
      set((state) => {
        state.notes.isSyncing = true;
      });

      try {
        const response = isGuid(id)
          ? await notesService.getById(id)
          : await notesService.getByShortId(id);
        const mappedNote = mapNoteFromApi(response.data);

        set((state) => {
          const existingIndex = state.notes.items.findIndex(
            (note) => note.id === mappedNote.id
          );
          if (existingIndex >= 0) {
            state.notes.items[existingIndex] = noteToListItem(mappedNote);
          }
          state.notes.detailedNote = mappedNote;

          if (
            state.notes.selectedId &&
            state.notes.selectedId !== mappedNote.id
          ) {
            state.notes.selectedId = mappedNote.id;
          }

          if (
            state.notes.formEditingId &&
            state.notes.formEditingId !== mappedNote.id &&
            state.notes.formEditingId === mappedNote.shortId
          ) {
            state.notes.formEditingId = mappedNote.id;
          }

          state.notes.isSyncing = false;
        });
      } catch (error) {
        set((state) => {
          state.notes.isSyncing = false;
          state.notes.error = error as ApiError;
        });
      }
    },

    createNote: async (data: CreateNoteData) => {
      set((state) => {
        state.notes.isSyncing = true;
        state.notes.error = null;
      });

      try {
        const response = await notesService.create({
          title: data.title,
          content: data.content,
          latitude: data.latitude ?? null,
          longitude: data.longitude ?? null,
          location: data.location,
          color: data.color,
          visibility: data.visibility,
          taskIds: data.taskIds,
        });

        const mappedNote = mapNoteFromApi(response.data.note);
        const listItem = noteToListItem(mappedNote);

        set((state) => {
          state.notes.items.unshift(listItem);
          state.notes.totalCount += 1;
          state.notes.selectedId = mappedNote.id;
          syncMapNote(state.notes.mapNotes, mappedNote);
          state.notes.isSyncing = false;
        });

        return mappedNote.id;
      } catch (error) {
        set((state) => {
          state.notes.isSyncing = false;
          state.notes.error = error as ApiError;
        });
        return null;
      }
    },

    updateNote: async (id: string, data: UpdateNoteData) => {
      set((state) => {
        state.notes.isSyncing = true;
      });

      try {
        const response = await notesService.update(id, {
          title: data.title,
          content: data.content,
          latitude: data.latitude ?? null,
          longitude: data.longitude ?? null,
          location: data.location,
          color: data.color,
          visibility: data.visibility,
          taskIds: data.taskIds,
          removeAnchorPoint: data.removeAnchorPoint,
        });

        const mappedNote = mapNoteFromApi(response.data.note);

        set((state) => {
          const existingIndex = state.notes.items.findIndex(
            (note) => note.id === mappedNote.id
          );
          if (existingIndex >= 0) {
            state.notes.items[existingIndex] = noteToListItem(mappedNote);
          }
          // Keep detailedNote in sync (match updateTask pattern)
          if (state.notes.detailedNote?.id === mappedNote.id) {
            state.notes.detailedNote = mappedNote;
          }
          // Keep map markers in sync without a refetch (mirror tasks slice)
          syncMapNote(state.notes.mapNotes, mappedNote);
          state.notes.isSyncing = false;
        });

        return true;
      } catch (error) {
        set((state) => {
          state.notes.isSyncing = false;
          state.notes.error = error as ApiError;
        });
        return false;
      }
    },

    deleteNote: async (id: string) => {
      set((state) => {
        state.notes.isSyncing = true;
      });

      try {
        await notesService.delete(id);

        set((state) => {
          state.notes.items = state.notes.items.filter(
            (note) => note.id !== id
          );
          state.notes.mapNotes = state.notes.mapNotes.filter(
            (mapItem) => mapItem.id !== id
          );
          if (state.notes.selectedId === id) {
            state.notes.selectedId = null;
          }
          // Clear detailedNote if it was the deleted note
          if (state.notes.detailedNote?.id === id) {
            state.notes.detailedNote = null;
          }
          state.notes.totalCount = Math.max(0, state.notes.totalCount - 1);
          state.notes.isSyncing = false;
        });

        return true;
      } catch (error) {
        set((state) => {
          state.notes.isSyncing = false;
          state.notes.error = error as ApiError;
        });
        return false;
      }
    },

    selectNote: (id: string | null) => {
      handleSelectItem(
        set,
        id,
        (draft, val) => {
          draft.notes.selectedId = val;
        },
        get().notes.detailedNote,
        (fetchId) => get().notes.fetchNoteById(fetchId),
        (draft) => {
          draft.notes.detailedNote = null;
        }
      );
    },

    selectItem: (id: string): void => {
      get().notes.selectNote(id);
    },

    // ===== ATTACHMENT ACTIONS =====

    uploadAttachment: async (noteId: string, file: File) => {
      set((state) => {
        state.notes.isSyncing = true;
      });

      try {
        await notesService.uploadAttachment(noteId, file);

        set((state) => {
          const note = state.notes.items.find((note) => note.id === noteId);
          if (note) {
            note.attachmentCount += 1;
          }
          state.notes.isSyncing = false;
        });

        return true;
      } catch (error) {
        set((state) => {
          state.notes.isSyncing = false;
          state.notes.error = error as ApiError;
        });
        return false;
      }
    },

    deleteAttachment: async (noteId: string, attachmentId: string) => {
      set((state) => {
        state.notes.isSyncing = true;
      });

      try {
        await notesService.deleteAttachment(noteId, attachmentId);

        set((state) => {
          const note = state.notes.items.find((note) => note.id === noteId);
          if (note) {
            note.attachmentCount = Math.max(0, note.attachmentCount - 1);
          }
          state.notes.isSyncing = false;
        });

        return true;
      } catch (error) {
        set((state) => {
          state.notes.isSyncing = false;
          state.notes.error = error as ApiError;
        });
        return false;
      }
    },

    uploadImage: async (noteId: string, file: File) => {
      try {
        const response = await notesService.uploadImage(noteId, file);
        return response.data.url;
      } catch (error) {
        set((state) => {
          state.notes.error = error as ApiError;
        });
        return null;
      }
    },

    // ===== TASK-LINKED NOTES =====

    fetchNotesByTaskId: async (taskId: string) => {
      try {
        const response = await notesService.getByTaskId(taskId);
        return response.data.map(mapNoteFromApi);
      } catch {
        return [];
      }
    },

    // ===== STATE HELPERS =====

    setLoading: (loading: boolean) => {
      set((state) => {
        state.notes.isLoading = loading;
      });
    },

    setError: (error: ApiError | null) => {
      set((state) => {
        state.notes.error = error;
      });
    },

    clearError: () => {
      set((state) => {
        state.notes.error = null;
      });
    },
  },
});
